import { useState, useEffect, useMemo, useRef, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Box, Button, TextField, Typography, CircularProgress, Switch, Autocomplete, FormControl, Select, MenuItem, Checkbox, LinearProgress, } from '@mui/material';
import { ReceiptLong as ReceiptLongIcon, Save as SaveIcon, CheckBox as CheckBoxIcon, CheckBoxOutlineBlank as CheckBoxBlankIcon, } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useRights } from '../context/RightsContext';
import { getCurrentFYear } from '../utils/financialYear';
import { APPBAR_HEIGHT } from '../utils/theme';

const C = { blue: '#1877F2', blueDark: '#1464CC', blueLight: '#E7F0FD', blueMid: '#D0E4FC', grey200: '#E5E7EB', grey300: '#D1D5DB', grey400: '#9CA3AF', grey600: '#4B5563', grey800: '#1F2937', arctic: '#FAFAFA', white: '#FFFFFF', surface: '#F4F6FB', danger: '#DC2626', ink: '#0f172a', sub: '#64748b', line: '#E4E6EB', page: '#F5F7FA', };

const ios = {
  width: 34, height: 19, p: 0,
  '& .MuiSwitch-switchBase': { p: 0, m: '2px',
    transition: 'transform 0.18s cubic-bezier(0.4,0,0.2,1)',
    '&.Mui-checked': { transform: 'translateX(15px)', color: C.white, '& + .MuiSwitch-track': { bgcolor: C.blue, opacity: 1 }, },
  },
  '& .MuiSwitch-thumb': { width: 15, height: 15, bgcolor: C.white, boxShadow: '0 1px 3px rgba(0,0,0,0.18)' },
  '& .MuiSwitch-track': { borderRadius: 10, bgcolor: C.grey300, opacity: 1 },
};

const CHARGE_COUNT = 5;
const TRANSTYPE_BASE = 200;

// Additional Charges are BookName-wise: each book has its own Additional Charge 1-5.
// ccode is the BookName stored in userdefinecharges (same codes as User Define Columns).
// billingOnly = shown for BOTH "Billing" and "Accounting" plans; !billingOnly = "Accounting" plan only.
const CHARGE_BOOKS = [
  { value: 'QUOTATION',        label: 'Quotation',        ccode: 'QUOT',     billingOnly: true },
  { value: 'SALE_ORDER',       label: 'Sale Order',       ccode: 'SORDER' },
  { value: 'CHALLAN',          label: 'Sale Challan',     ccode: 'CHAL',     billingOnly: true },
  { value: 'SALE_INVOICE',     label: 'Sale Invoice',     ccode: 'SALE',     billingOnly: true },
  { value: 'PROFORMA_INVOICE', label: 'Proforma Invoice', ccode: 'PROFORMA' },
  { value: 'CREDIT_NOTE',      label: 'Credit Note',      ccode: 'CREDIT',   billingOnly: true },
  { value: 'PURCHASE_ORDER',   label: 'Purchase Order',   ccode: 'PORDER' },
  { value: 'PURCHASE_CHALLAN', label: 'Purchase Challan', ccode: 'PCHAL' },
  { value: 'PURCHASE_INVOICE', label: 'Purchase Invoice', ccode: 'PURC' },
  { value: 'CREDIT_EXPENSE',   label: 'Credit Expense',   ccode: 'CREXP' },
  { value: 'DEBIT_NOTE',       label: 'Debit Note',       ccode: 'DEBIT' },
];

const blankCharge = (srNo) => ({
  SrNo: srNo,
  TransType: TRANSTYPE_BASE + srNo - 1,
  ChargeName: '',
  TaxEnabled: false,
  TaxId: null,
  HsnId: null,
  PartyId: null,
  ColToBeAsk: false,
  tax: null,
  hsn: null,
  client: null,
});

export default function UserDefineCharges({ connectionString }) {
  const { selectedCompany, user } = useAuth();
  const companyId = selectedCompany?.CompId;
  const userId = user?.UserId;
  const navigate = useNavigate();
  const { canAccess, showAccessDenied, rightsReady, rightsLoading } = useRights();

  useEffect(() => {
    if (!rightsReady || rightsLoading) return;
    if (canAccess && !canAccess('UserDefine', 'view')) {
      showAccessDenied?.();
      navigate('/main/home', { replace: true });
    }
  }, [rightsReady, rightsLoading, canAccess, showAccessDenied, navigate]);

  const planName = selectedCompany?.planName;
  const visibleBooks = useMemo(
    () => (planName === 'Billing' ? CHARGE_BOOKS.filter(b => b.billingOnly) : CHARGE_BOOKS),
    [planName]
  );
  const [selectedBook, setSelectedBook] = useState('SALE_INVOICE');
  // If the plan does not allow the current selection, fall back to the first book it allows.
  useEffect(() => {
    if (visibleBooks.length > 0 && !visibleBooks.some(b => b.value === selectedBook)) {
      setSelectedBook(visibleBooks[0].value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleBooks]);
  const currentBook = CHARGE_BOOKS.find(b => b.value === selectedBook);
  const bookCode = currentBook?.ccode;

  const [charges, setCharges] = useState(() =>
    Array.from({ length: CHARGE_COUNT }, (_, i) => blankCharge(i + 1))
  );
  const [chargeErrors, setChargeErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // true right after a successful Save and until something is edited / another book is picked:
  // Save stays disabled meanwhile, and "Apply to all" is only enabled while this is true.
  const [isSaved, setIsSaved] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyMsg, setApplyMsg] = useState(null);
  const [gstOptions, setGstOptions] = useState([]);
  const [hsnOptions, setHsnOptions] = useState([]);

  useEffect(() => {
    if (!companyId || !connectionString || !bookCode) return;
    setLoading(true);
    axios.get(`${connectionString}/api/userdefinecharges?companyId=${companyId}&fYear=${getCurrentFYear()}&bookName=${bookCode}`)
      .then(res => {
        if (res.data.success && res.data.charges) {
          // Global rows first, this book's own rows after them — so the book's own charge wins for a slot.
          const dbCharges = [...res.data.charges].sort((a, b) => (a.BookName ? 1 : 0) - (b.BookName ? 1 : 0));
          const slots = Array.from({ length: CHARGE_COUNT }, (_, i) => blankCharge(i + 1));
          dbCharges.forEach(dbCharge => {
            const idx = Number(dbCharge.SrNo) - 1;
            if (idx >= 0 && idx < CHARGE_COUNT) {
              slots[idx] = {
                ...dbCharge,
                TaxEnabled: dbCharge.TaxEnabled === 1 || dbCharge.TaxEnabled === true,
                ColToBeAsk: dbCharge.ColToBeAsk === 1 || dbCharge.ColToBeAsk === true,
                tax: dbCharge.TaxId != null ? { TaxID: dbCharge.TaxId, Taxcode: dbCharge.Taxcode } : null,
                hsn: dbCharge.HsnId != null ? { HSNId: dbCharge.HsnId, HSNNo: dbCharge.HSNNo } : null,
                client: dbCharge.PartyId != null ? { Id: dbCharge.PartyId, FirmName: dbCharge.clientName } : null,
              };
            }
          });
          setCharges(slots);
        }
      })
      .catch(err => console.error('Failed to fetch charges:', err))
      .finally(() => setLoading(false));
  }, [companyId, connectionString, bookCode]);

  useEffect(() => {
    if (!companyId || !connectionString) return;
    axios.get(`${connectionString}/api/product-get-data/api/gst`, { params: { companyId } })
      .then(res => setGstOptions(Array.isArray(res.data) ? res.data : []))
      .catch(() => setGstOptions([]));
    axios.get(`${connectionString}/api/product-get-data/api/hsn`, { params: { companyId } })
      .then(res => setHsnOptions(Array.isArray(res.data) ? res.data : []))
      .catch(() => setHsnOptions([]));
  }, [companyId, connectionString]);

  // ✅ NEW: Check if submit button should be disabled based on validation rules
  const isSubmitDisabled = useMemo(() => {
    // Button is disabled if any charge violates the rules:
    // 1. If ChargeName is filled → HSN, Client are required
    // 2. If ColToBeAsk (checkbox) is selected → ChargeName, HSN, Client are required
    // 3. If TaxEnabled → ChargeName, HSN, Client, and Tax are required
    // 4. If any of HSN/Tax/Client is selected → ChargeName must be filled
    for (const c of charges) {
      const hasChargeName = c.ChargeName && c.ChargeName.trim();
      const hasHsn = !!c.hsn;
      const hasTax = !!c.tax;
      const hasClient = !!c.client;
      const isChecked = c.ColToBeAsk;
      const isTaxEnabled = c.TaxEnabled;

      // Rule 1: If ChargeName filled → HSN and Client required
      if (hasChargeName && (!hasHsn || !hasClient)) {
        return true;
      }

      // Rule 2: If checkbox selected → ChargeName, HSN, Client required
      if (isChecked && (!hasChargeName || !hasHsn || !hasClient)) {
        return true;
      }

      // Rule 3: If TaxEnabled → ChargeName, HSN, Client, Tax required
      if (isTaxEnabled && (!hasChargeName || !hasHsn || !hasClient || !hasTax)) {
        return true;
      }

      // Rule 4: If any HSN/Tax/Client selected → ChargeName required
      if ((hasHsn || hasTax || hasClient) && !hasChargeName) {
        return true;
      }

      // Rule 5: If Tax is enabled but not selected → disable
      if (isTaxEnabled && !hasTax) {
        return true;
      }

      // Rule 6: If HSN selected but not Client → disable (and vice versa)
      if ((hasHsn || hasClient) && (!hasHsn || !hasClient)) {
        return true;
      }
    }
    return false;
  }, [charges]);

  const updateCharge = useCallback((idx, patch) => {
    setIsSaved(false);
    setApplyMsg(null);
    setCharges(prev => prev.map((c, i) => i === idx ? { ...c, ...patch } : c));
    setChargeErrors(prev => {
      const cur = prev[idx];
      if (!cur) return prev;
      const nextSlot = { ...cur };
      if (patch.client) delete nextSlot.client;
      if (patch.hsn) delete nextSlot.hsn;
      if (patch.tax) delete nextSlot.tax;
      if (Object.keys(nextSlot).length === Object.keys(cur).length) return prev;
      const next = { ...prev };
      if (Object.keys(nextSlot).length > 0) next[idx] = nextSlot; else delete next[idx];
      return next;
    });
  }, []);

  // Payload that saves the current charge slots for the given book (BookName code).
  const buildChargePayload = (code) => {
      const chargeRows = charges.map((c, idx) => ({
        CompID: companyId,
        SrNo: idx + 1,
        TransType: 200 + idx,
        ColToBeAsk: c.ColToBeAsk,
        ChargeName: c.ChargeName || null,
        TaxEnabled: c.TaxEnabled,
        TaxId: c.tax?.TaxID ?? null,
        HsnId: c.hsn?.HSNId ?? null,
        PartyId: c.client?.Id ?? null,
        EditUserId: userId ?? null,
        EditUserDateTime: new Date().toISOString(),
      }));

      // Each description is tied to its own slot's TransType (taken from the slot position BEFORE filtering,
      // so a charge in slot 3 never renames slot 1's productmaster item when slots 1-2 are empty).
      const chargeDescriptions = charges
        .map((c, idx) => ({ c, idx }))
        .filter(({ c }) => c.ChargeName && c.ChargeName.trim())
        .map(({ c, idx }) => ({
          transType: 200 + idx,
          description: c.ChargeName,
        }));

      const payload = {
        companyId,
        fYear: getCurrentFYear(),
        bookName: code,
        additionalCharges: chargeRows,
        chargeDescriptions,
      };
      return payload;
  };

  const handleSave = async () => {
    if (!companyId) return;

    const invalidCharges = {};
    charges.forEach((c, i) => {
      if (!c.ColToBeAsk) return;
      const errs = {};
      if (!c.client) errs.client = true;
      if (!c.hsn) errs.hsn = true;
      if (c.TaxEnabled && !c.tax) errs.tax = true;
      if (Object.keys(errs).length > 0) invalidCharges[i] = errs;
    });
    if (Object.keys(invalidCharges).length > 0) {
      setChargeErrors(invalidCharges);
      return;
    }
    setChargeErrors({});

    setSaving(true);

    try {
      const payload = buildChargePayload(bookCode);

      const res = await axios.post(`${connectionString}/api/userdefinecharges`, payload);
      if (res.data.success) {
        setIsSaved(true);
      }
    } catch (err) {
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  // Applies the charges just saved for the active book to every OTHER book (module) the plan allows.
  const handleApplyToAll = async () => {
    if (!companyId || !isSaved) return;
    setApplying(true);
    setApplyMsg(null);
    try {
      const targets = visibleBooks.filter(b => b.ccode !== bookCode);
      for (const b of targets) {
        await axios.post(`${connectionString}/api/userdefinecharges`, buildChargePayload(b.ccode));
      }
      setApplyMsg({ ok: true, text: `Applied to ${targets.length} other module${targets.length === 1 ? '' : 's'}` });
    } catch (err) {
      console.error('Apply to all error:', err);
      setApplyMsg({ ok: false, text: 'Could not apply to all modules. Please try again.' });
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ height: `calc(100vh - ${APPBAR_HEIGHT}px)`, bgcolor: C.page, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const configuredCount = charges.filter(c => c.ChargeName && c.ChargeName.trim()).length;

  return (
    <Box className="udc-root" sx={{ height: `calc(100vh - ${APPBAR_HEIGHT}px)`, bgcolor: C.page, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
      <UdcStyle />
      {saving && <LinearProgress sx={udcBarSx} />}

      <Box sx={{ flex: 1, minHeight: 0, position: 'relative', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: { xs: 1.5, sm: 3 }, py: 2 }}>
        <Box sx={{ maxWidth: 1680, mx: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2, animation: 'udcRise .5s cubic-bezier(.33,0,.2,1) backwards' }}>
            <Box>
              <Typography sx={{ color: C.blue, fontSize: '0.74rem', fontWeight: 700, letterSpacing: '1.6px', textTransform: 'uppercase' }}>
                Configuration
              </Typography>
              <Typography sx={{ color: C.ink, fontWeight: 700, fontSize: { xs: '1.35rem', sm: '1.6rem' }, letterSpacing: '-0.6px', lineHeight: 1.15, mt: 0.25 }}>
                Additional Charges
              </Typography>
              <Typography sx={{ color: C.sub, fontSize: '0.92rem', mt: 0.5 }}>
                Configure up to 5 extra charge fields for the selected book
              </Typography>
            </Box>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75, borderRadius: 999, bgcolor: C.blueLight, color: C.blueDark, fontSize: '0.8rem', fontWeight: 600 }}>
              <ReceiptLongIcon sx={{ fontSize: 17 }} />
              {configuredCount} of {CHARGE_COUNT} configured
            </Box>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <Select
                value={selectedBook}
                onChange={(e) => { setChargeErrors({}); setIsSaved(false); setApplyMsg(null); setSelectedBook(e.target.value); }}
                disabled={saving}
                sx={{ borderRadius: '10px', bgcolor: '#FAFBFC', fontSize: '0.88rem', fontWeight: 600 }}
              >
                {visibleBooks.map(b => (
                  <MenuItem key={b.value} value={b.value} sx={{ fontSize: '0.88rem' }}>{b.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))', lg: 'repeat(5, minmax(0, 1fr))' }, gap: 2, alignItems: 'start' }}>
            {charges.map((charge, idx) => (
              <AdditionalChargeBlock
                key={idx}
                index={idx + 1}
                delay={idx * 0.05}
                charge={charge}
                errors={chargeErrors[idx] || NO_ERRORS}
                gstOptions={gstOptions}
                hsnOptions={hsnOptions}
                connectionString={connectionString}
                companyId={companyId}
                onChange={updateCharge}
              />
            ))}
          </Box>
        </Box>
      </Box>
      {saving && <SavingOverlay />}
      </Box>

      <Box sx={{ bgcolor: 'rgba(255,255,255,0.94)', backdropFilter: 'blur(8px)', borderTop: `1px solid ${C.line}`, px: { xs: 2, sm: 3 }, py: 1.5, display: 'flex', gap: 1.5, justifyContent: 'flex-end', flexShrink: 0 }}>
        <Button
          variant="outlined"
          onClick={() => navigate(-1)}
          sx={{
            height: 40, px: 2.5, borderRadius: '10px', textTransform: 'none', fontWeight: 600, fontSize: '0.88rem',
            color: C.sub, borderColor: C.line,
            '&:hover': { borderColor: C.blue, color: C.blue, bgcolor: C.blueLight },
          }}
        >
          Cancel
        </Button>
        {applyMsg && (
          <Typography sx={{ mr: 'auto', alignSelf: 'center', fontSize: '0.84rem', fontWeight: 600, color: applyMsg.ok ? '#16a34a' : '#dc2626' }}>
            {applyMsg.text}
          </Typography>
        )}
        <Button
          variant="outlined"
          onClick={handleApplyToAll}
          disabled={!isSaved || applying || saving || loading}
          startIcon={applying ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : null}
          sx={{
            height: 40, px: 2.5, borderRadius: '10px', textTransform: 'none', fontWeight: 600, fontSize: '0.88rem', whiteSpace: 'nowrap',
            color: C.blue, borderColor: C.blue,
            '&:hover': { borderColor: C.blueDark, color: C.blueDark, bgcolor: C.blueLight },
            '&.Mui-disabled': { color: C.grey400, borderColor: C.line, cursor: 'not-allowed', pointerEvents: 'auto' },
          }}
        >
          {applying ? 'Applying…' : 'Apply to all'}
        </Button>
        <Button
          variant="contained"
          disableElevation
          startIcon={saving ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <SaveIcon sx={{ fontSize: '1.05rem' }} />}
          onClick={handleSave}
          disabled={loading || saving || isSubmitDisabled || isSaved}
          sx={{
            height: 40, px: 3, borderRadius: '10px', textTransform: 'none', fontWeight: 700, fontSize: '0.88rem', whiteSpace: 'nowrap',
            background: `linear-gradient(135deg, ${C.blue} 0%, ${C.blueDark} 100%)`,
            boxShadow: '0 6px 18px rgba(24,119,242,0.3)',
            transition: 'transform .2s ease, box-shadow .2s ease',
            '&:hover': { boxShadow: '0 10px 24px rgba(24,119,242,0.42)', transform: 'translateY(-2px)' },
            '&:active': { transform: 'translateY(0) scale(.99)' },
            '&.Mui-disabled': { background: '#F0F2F5', color: C.grey400, boxShadow: 'none', cursor: 'not-allowed', pointerEvents: 'auto' },
          }}
        >
          {saving ? 'Saving…' : 'Save Configuration'}
        </Button>
      </Box>
    </Box>
  );
}

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px', bgcolor: '#FAFBFC', fontSize: '0.84rem',
    transition: 'background-color .2s ease, box-shadow .2s ease',
    '& fieldset': { borderColor: C.line, transition: 'border-color .2s ease' },
    '&:hover fieldset': { borderColor: C.blue },
    '&.Mui-focused': { bgcolor: '#fff', boxShadow: '0 0 0 4px rgba(24,119,242,0.12)' },
    '&.Mui-focused fieldset': { borderColor: C.blue, borderWidth: 2 },
  },
  '& .MuiInputBase-input': { fontSize: '0.84rem' },
};

const acInputSx = {
  ...fieldSx,
  '& .MuiInputBase-input': { fontSize: '0.84rem', py: '4px !important' },
};

const selectSx = {
  borderRadius: '10px', bgcolor: '#FAFBFC', fontSize: '0.84rem',
  '& .MuiSelect-select': { py: '9px' },
  '& fieldset': { borderColor: C.line },
  '&:hover fieldset': { borderColor: C.blue },
  '&.Mui-focused': { bgcolor: '#fff', boxShadow: '0 0 0 4px rgba(24,119,242,0.12)' },
  '&.Mui-focused fieldset': { borderColor: C.blue, borderWidth: 2 },
};

const popupPaperSx = {
  borderRadius: '10px', border: `1px solid ${C.line}`, boxShadow: '0 8px 24px rgba(15,23,42,0.12)',
  '& .MuiAutocomplete-option': { fontSize: '0.82rem' },
};

function RequiredLabel({ children, required }) {
  return (
    <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: C.sub, letterSpacing: '0.6px', textTransform: 'uppercase', mb: 0.75 }}>
      {children}{required && <span style={{ color: C.danger }}> *</span>}
    </Typography>
  );
}

function FieldError({ children }) {
  return (
    <Typography sx={{ fontSize: '0.74rem', color: C.danger, mt: 0.5 }}>
      {children}
    </Typography>
  );
}

const udcBarSx = {
  position: 'absolute', top: 0, left: 0, right: 0, height: 3, zIndex: 20, bgcolor: 'transparent',
  '& .MuiLinearProgress-bar': { bgcolor: C.blue },
};

function UdcStyle() {
  return (
    <style>{[
      "@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&display=swap');",
      ".udc-root, .udc-root * { font-family: 'IBM Plex Sans', -apple-system, 'Segoe UI', Roboto, sans-serif !important; }",
      "@keyframes udcRise { from { opacity: 0; transform: translate3d(0, 8px, 0); } to { opacity: 1; transform: translate3d(0, 0, 0); } }",
      "@keyframes udcFade { from { opacity: 0; } to { opacity: 1; } }",
    ].join("\n")}</style>
  );
}

function SavingOverlay() {
  return (
    <Box sx={{
      position: 'absolute', inset: 0, zIndex: 15, bgcolor: 'rgba(245,247,250,0.72)', backdropFilter: 'blur(3px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'udcFade .2s ease',
    }}>
      <Box sx={{
        bgcolor: C.white, border: `1px solid ${C.line}`, borderRadius: 3, boxShadow: '0 12px 32px rgba(15,23,42,0.14)',
        px: 4, py: 3, minWidth: 260, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5,
        animation: 'udcRise .3s cubic-bezier(.33,0,.2,1) backwards',
      }}>
        <CircularProgress size={34} thickness={4} sx={{ color: C.blue }} />
        <Typography sx={{ color: C.ink, fontWeight: 700, fontSize: '0.95rem' }}>
          Saving configuration…
        </Typography>
        <LinearProgress sx={{ width: '100%', height: 4, borderRadius: 2, bgcolor: C.blueLight, '& .MuiLinearProgress-bar': { bgcolor: C.blue } }} />
      </Box>
    </Box>
  );
}

const NO_ERRORS = {};
const EASE = 'cubic-bezier(.2,.8,.2,1)';

const TaxSelect = memo(function TaxSelect({ enabled, tax, hasError, gstOptions, onSelect }) {
  return (
    <FormControl size="small" fullWidth disabled={!enabled} error={hasError}>
      <Select
        displayEmpty
        value={tax?.TaxID ?? ''}
        renderValue={(selected) => {
          if (!selected || selected === '') return <span style={{ color: C.grey400 }}>Select Tax</span>;
          const found = gstOptions.find(g => g.TaxID === selected) || tax;
          return found?.Taxcode || selected;
        }}
        onChange={e => onSelect(gstOptions.find(g => g.TaxID === e.target.value) || null)}
        MenuProps={{ slotProps: { paper: { sx: popupPaperSx } } }}
        sx={selectSx}
      >
        <MenuItem value="" disabled sx={{ fontSize: '0.82rem' }}>Select Tax</MenuItem>
        {gstOptions.map(g => (
          <MenuItem key={g.TaxID} value={g.TaxID} sx={{ fontSize: '0.82rem' }}>
            {g.Taxcode}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
});

const HsnField = memo(function HsnField({ value, options, hasError, onSelect }) {
  return (
    <Autocomplete
      size="small"
      value={value}
      onChange={(e, newVal) => onSelect(newVal)}
      options={options}
      getOptionLabel={opt => opt?.HSNNo ? `${opt.HSNNo}${opt.HSNDescription ? ' - ' + opt.HSNDescription : ''}` : ''}
      isOptionEqualToValue={(opt, val) => opt.HSNId === val?.HSNId}
      slotProps={{ paper: { sx: popupPaperSx } }}
      renderInput={params => (
        <TextField
          {...params}
          placeholder="Enter HSN/SAC"
          error={hasError}
          sx={acInputSx}
        />
      )}
    />
  );
});

const ClientField = memo(function ClientField({ connectionString, companyId, value, hasError, onSelect }) {
  return (
    <ClientAutocomplete
      connectionString={connectionString}
      companyId={companyId}
      value={value}
      onChange={onSelect}
      error={hasError}
    />
  );
});

const AdditionalChargeBlock = memo(function AdditionalChargeBlock({ index, charge, errors, gstOptions, hsnOptions, connectionString, companyId, onChange, delay = 0 }) {
  const idx = index - 1;
  const chargeLabel = charge.ChargeName || `Charge ${index}`;
  const active = charge.ColToBeAsk;

  const handleAsk = useCallback(e => onChange(idx, { ColToBeAsk: e.target.checked }), [onChange, idx]);
  const handleName = useCallback(e => onChange(idx, { ChargeName: e.target.value }), [onChange, idx]);
  const handleTaxEnabled = useCallback(e => onChange(idx, { TaxEnabled: e.target.checked }), [onChange, idx]);
  const handleTax = useCallback(tax => onChange(idx, { tax }), [onChange, idx]);
  const handleHsn = useCallback(hsn => onChange(idx, { hsn }), [onChange, idx]);
  const handleClient = useCallback(client => onChange(idx, { client }), [onChange, idx]);

  return (
    <Box sx={{
      bgcolor: C.white,
      border: `1px solid ${active ? C.blue : C.line}`,
      borderRadius: '16px',
      boxShadow: active
        ? '0 8px 24px rgba(24,119,242,0.14)'
        : '0 1px 2px rgba(15,23,42,0.04), 0 4px 14px rgba(15,23,42,0.04)',
      transition: `border-color .25s ${EASE}, box-shadow .25s ${EASE}, transform .25s ${EASE}`,
      animation: `udcRise .5s ${EASE} ${delay}s backwards`,
      overflow: 'hidden',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: active
          ? '0 12px 30px rgba(24,119,242,0.18)'
          : '0 2px 4px rgba(15,23,42,0.05), 0 12px 28px rgba(15,23,42,0.09)',
      },
      '&:focus-within': { borderColor: C.blue },
    }}>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1,
        bgcolor: active ? C.blueLight : C.white,
        borderBottom: `1px solid ${active ? C.blueMid : C.line}`,
        transition: `background-color .25s ${EASE}, border-color .25s ${EASE}`,
      }}>
        <Box sx={{
          width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '0.85rem', fontWeight: 700,
          bgcolor: active ? C.blue : '#F0F2F5', color: active ? C.white : C.sub,
          boxShadow: active ? `0 0 0 4px ${C.blueMid}` : '0 0 0 0 transparent',
          transition: `background-color .25s ${EASE}, color .25s ${EASE}, box-shadow .25s ${EASE}`,
        }}>
          {index}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography noWrap sx={{ color: C.ink, fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.25 }}>
            {chargeLabel}
          </Typography>
          <Typography sx={{ color: C.sub, fontSize: '0.74rem' }}>
            Additional Charge {index}
          </Typography>
        </Box>
        <Checkbox
          size="small"
          checked={charge.ColToBeAsk}
          onChange={handleAsk}
          inputProps={{ 'aria-label': `Select charge ${index}` }}
          icon={<CheckBoxBlankIcon sx={{ fontSize: '1.3rem', color: C.grey300 }} />}
          checkedIcon={<CheckBoxIcon sx={{ fontSize: '1.3rem', color: C.blue }} />}
          sx={{ p: 0.5 }}
        />
      </Box>

      <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box>
          <RequiredLabel required>Charge name</RequiredLabel>
          <TextField
            size="small"
            fullWidth
            placeholder={`Additional Charge ${index}`}
            value={charge.ChargeName}
            onChange={handleName}
            sx={fieldSx}
          />
        </Box>

        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.5, mb: 1.25, borderRadius: '10px', bgcolor: '#FAFBFC', border: `1px solid ${C.line}` }}>
            <Switch
              checked={charge.TaxEnabled}
              onChange={handleTaxEnabled}
              size="small"
              sx={ios}
            />
            <Typography noWrap sx={{ fontSize: '0.82rem', color: C.grey600 }}>
              Enable tax for {chargeLabel}
            </Typography>
          </Box>
          <RequiredLabel required={charge.TaxEnabled}>Select Tax</RequiredLabel>
          <TaxSelect
            enabled={charge.TaxEnabled}
            tax={charge.tax}
            hasError={!!errors.tax}
            gstOptions={gstOptions}
            onSelect={handleTax}
          />
          {errors.tax && <FieldError>Tax is required for {chargeLabel}</FieldError>}
        </Box>

        <Box>
          <RequiredLabel required>HSN/SAC</RequiredLabel>
          <HsnField
            value={charge.hsn}
            options={hsnOptions}
            hasError={!!errors.hsn}
            onSelect={handleHsn}
          />
          {errors.hsn && <FieldError>HSN/SAC is required for {chargeLabel}</FieldError>}
          <Typography sx={{ fontSize: '0.74rem', color: C.grey400, mt: 0.75 }}>
            You can change this while creating transactions
          </Typography>
        </Box>

        <Box>
          <RequiredLabel required>Separate posting client</RequiredLabel>
          <ClientField
            connectionString={connectionString}
            companyId={companyId}
            value={charge.client}
            hasError={!!errors.client}
            onSelect={handleClient}
          />
          {errors.client && <FieldError>Separate posting client is required for {chargeLabel}</FieldError>}
        </Box>
      </Box>
    </Box>
  );
});

function ClientAutocomplete({ connectionString, companyId, value, onChange, error }) {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!companyId || !connectionString) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      setLoading(true);
      axios.get(`${connectionString}/api/company-search`, {
        params: { field: 'FirmName', search: inputValue, companyId },
      })
        .then(res => {
          const data = Array.isArray(res.data.data) ? res.data.data : [];
          setOptions(data.map(row => ({ Id: row.id, FirmName: row.FirmName })));
        })
        .catch(() => setOptions([]))
        .finally(() => setLoading(false));
    }, 300);

    return () => clearTimeout(debounceRef.current);
  }, [inputValue, companyId, connectionString]);

  return (
    <Autocomplete
      size="small"
      value={value}
      onChange={(e, newVal) => {
        onChange(newVal);
        // When selecting an item, clear the search input to show the selected value
        if (newVal?.FirmName) {
          setInputValue(newVal.FirmName);
        }
      }}
      inputValue={inputValue}
      onInputChange={(e, newInput) => {
        setInputValue(newInput);
      }}
      options={options}
      loading={loading}
      filterOptions={x => x}
      getOptionLabel={opt => opt?.FirmName || ''}
      isOptionEqualToValue={(opt, val) => opt.Id === val?.Id}
      noOptionsText={loading ? 'Loading...' : 'No options'}
      renderInput={params => (
        <TextField
          {...params}
          placeholder="Select Separate Posting client"
          error={error}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={12} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          sx={acInputSx}
        />
      )}
    />
  );
}
